import printIcon from "@assets/images/print-inquiry.svg";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@material-ui/core";
import {
  changeOrderPath,
  notRefchangeOrderPath
} from "@saleor/changeOrderDomestic/urls";
import Checkbox from "@saleor/components/Checkbox";
import Link from "@saleor/components/Link";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { BIG_ROW_NUMBER_SET } from "@saleor/components/TablePagination/constants";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import {
  DomesticOrderConfirmationFilterInput,
  ListOrderConfirmationSapQuery,
  usePrintPdfOrderConfirmMutation
} from "@saleor/graphql";
import useNavigator from "@saleor/hooks/useNavigator";
import { renderCollection } from "@saleor/misc";
import BottomBarSendMail from "@saleor/OrderConfirm/components/BottomBarSendMail";
import ModalSendMailMultiple from "@saleor/OrderConfirm/components/ModalSendMailMultiple";
import {
  ORDER_REPORT_LANGUAGE_CHOICES,
  OrderReportLanguageEnum
} from "@saleor/OrderConfirm/const";
import { OrderConfirmListSortField } from "@saleor/OrderConfirm/urls";
import { ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import useStyles from "./styles";

export interface IOrderConfirmListTableProps
  extends ListProps,
    SortPage<OrderConfirmListSortField> {
  data: ListOrderConfirmationSapQuery["listOrderConfirmationSap"];
  dataSearchList: ListOrderConfirmationSapQuery["listOrderConfirmationSap"];
  loading?: boolean;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  group?: number | string;
  selectSendMail: {
    add: (data: string) => void;
    set: (data: string[]) => void;
    isSelected: (data: string) => boolean;
    reset: () => void;
    toggle: (data: string) => void;
    remove: (data: string) => void;
    listElements: string[];
  };
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  filter: DomesticOrderConfirmationFilterInput;
}

const OrderConfirmListTable: React.FC<IOrderConfirmListTableProps> = props => {
  const {
    data,
    dataSearchList,
    disabled,
    settings,
    pageInfo,
    totalCount,
    latestPageItemNumber,
    sort,
    selectSendMail,
    filter,
    onSort,
    onPreviousPage,
    onNextPage,
    onUpdateListSettings,
    onNextLastPage,
    onPreviousFirstPage
  } = props;
  const intl = useIntl();
  const classes = useStyles();
  const navigate = useNavigator();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [openSendMail, setOpenSendMail] = useState<boolean>(false);
  const [selectedReportLanguage, setSelectedReportLanguage] = useState<
    OrderReportLanguageEnum
  >(OrderReportLanguageEnum.TH);

  const { isSelected, listElements, reset, toggle, set } = selectSendMail;

  useEffect(() => {
    handleCheckboxAll();
  }, []);

  const listOrderLineId = useMemo(() => {
    const arr = [];
    data.forEach(item => {
      item.orderLines.forEach(line => {
        arr.push(`${item.soNo}_${line.itemNo}`);
      });
    });
    return arr;
  }, [data]);

  const soldToList = useMemo(() => {
    const mappingObjectSoldTo = {};
    dataSearchList.forEach(order => {
      const lineIds = order.orderLines.map(
        line => `${order.soNo}_${line.itemNo}`
      );
      for (const line of lineIds) {
        if (listElements.includes(line)) {
          const soldToCode = order.orderLines[0]?.soldToCode || "";
          const soldToName = order.orderLines[0]?.soldToName || "";
          const saleOrg = order.orderLines[0]?.saleOrg || "";
          const contractNo = order.contractNo || "";
          if (mappingObjectSoldTo[soldToCode]) {
            mappingObjectSoldTo[soldToCode].orderIds.push(order.soNo);
            mappingObjectSoldTo[soldToCode].orderLineIds.push(line);
            mappingObjectSoldTo[soldToCode].contractNo.push(contractNo);
            mappingObjectSoldTo[soldToCode].saleOrg.push(saleOrg);
          } else {
            mappingObjectSoldTo[soldToCode] = {
              soldToCode,
              soldToName,
              orderIds: [order.soNo],
              orderLineIds: [line],
              saleOrg: [saleOrg],
              contractNo: [contractNo]
            };
          }
        }
      }
    });
    const arrListSoldTo = [];
    Object.entries(mappingObjectSoldTo).forEach(([key, _val]) => {
      const val = mappingObjectSoldTo[key];
      const item = {
        soldToCode: val.soldToCode,
        soldToName: val.soldToName,
        orderIds: Array.from(new Set(val.orderIds)),
        orderLineIds: Array.from(new Set(val.orderLineIds)),
        saleOrg: Array.from(new Set(val.saleOrg)),
        contractNo: Array.from(new Set(val.contractNo))
      };
      arrListSoldTo.push(item);
    });
    return arrListSoldTo;
  }, [listElements, dataSearchList]);

  const renderStatus = status => {
    if (status === "พร้อมส่ง") {
      return (
        <MyTypography variant={"caption"} className={classes.statusReady}>
          พร้อมส่ง
        </MyTypography>
      );
    }
    if (status === "รอผลิต") {
      return (
        <MyTypography variant={"caption"} className={classes.statusInProgress}>
          รอผลิต
        </MyTypography>
      );
    }
    if (status === "ยกเลิก") {
      return (
        <MyTypography variant={"caption"} className={classes.statusCancel}>
          ยกเลิก
        </MyTypography>
      );
    }
    return "";
  };

  const handleCheckboxAll = () => {
    if (listElements.length === listOrderLineId.length) {
      reset();
      return;
    }
    set(listOrderLineId);
  };

  const [printOrderConfirm] = usePrintPdfOrderConfirmMutation({
    onCompleted: data => {
      const link = document.getElementById("download") as HTMLAnchorElement;
      link.href = `data:application/octet-stream;base64,${data.printPdfOrderConfirmation?.exportedFileBase64}`;
      link.target = "_self";
      link.download = data.printPdfOrderConfirmation?.fileName;
      link.click();
    }
  });

  const handlePrintOrderConfirm = () => {
    const dataToFilter = cloneDeep(data);

    // Filter order line selected
    dataToFilter.forEach(soNoItem => {
      soNoItem.orderLines = soNoItem.orderLines.filter(orderItem =>
        listElements.includes(`${soNoItem.soNo}_${orderItem.itemNo}`)
      );
    });
    // Filter soNo have order line selected
    const dataToPrint = dataToFilter.filter(
      soNoItem => soNoItem.orderLines.length
    );
    // Call api to print
    dataToPrint.forEach(soNoItem => {
      const soNoItemClean = {
        ...soNoItem,
        createdDate: soNoItem.orderDate,
        paymentTermDesc: soNoItem.paymentMethodName,
        itemNo: ["10"],
        shipTo: soNoItem.shipToCode,
        saleOrg: soNoItem.orderLines[0].saleOrg,
        __typename: undefined,
        indexData: undefined,
        indexNumber: undefined,
        orderDate: undefined,
        paymentMethodName: undefined,
        salesOrgName: undefined,
        soldToName: undefined,
        shipToCode: undefined
      };

      printOrderConfirm({
        variables: {
          reportLanguage: selectedReportLanguage,
          orders: {
            ...soNoItemClean,
            orderLines: soNoItemClean.orderLines.map(line => ({
              ...line,
              orderQty: String(line.orderQty),
              __typename: undefined,
              saleOrg: undefined,
              soldTo: line.soldToCode,
              confirmQty: String(line.confirmQuantity),
              nonConfirmQty: String(line.nonConfirmQuantity),
              requestDate: undefined,
              confirmQuantity: undefined,
              nonConfirmQuantity: undefined,
              soldToCode: undefined,
              soldToName: undefined,
              remarkOrder: undefined,
              confirmDate: undefined,
              iplanConfirmDate: line.confirmDate,
              firstDeliveryDate: line.requestDate,
              remark: line.remarkOrder
            }))
          }
        }
      });
    });
  };

  const viewOrder = item => {
    if (item?.isNotRef) {
      navigate(notRefchangeOrderPath(item?.soNo));
    } else {
      navigate(changeOrderPath(item?.soNo));
    }
  };

  const handleChangeReportLanguage = (
    event: React.ChangeEvent<{ value: string }>
  ) => {
    const language = (event?.target?.value ?? "") as OrderReportLanguageEnum;
    setSelectedReportLanguage(language);
  };

  return (
    <>
      <div className={classes.header}>
        <MyTypography className={classes.title} variant="h7">
          {`รายการที่เกี่ยวข้อง (${totalCount || 0})`}
        </MyTypography>
        <div>
          <SingleSelectField
            choices={ORDER_REPORT_LANGUAGE_CHOICES}
            value={selectedReportLanguage}
            InputProps={{
              inputProps: { "data-testid": "reportLanguage-select" },
              classes: { root: classes.selectLanguageField }
            }}
            onChange={handleChangeReportLanguage}
            name={"language"}
          />
        </div>
      </div>
      <div className={classes.root} ref={containerRef}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCellHeader
                  rowSpan={2}
                  align={"center"}
                  className={classNames(
                    classes.col60,
                    classes.tableHeader,
                    classes.borderRight,
                    classes.cellFreeze1_Admin
                  )}
                >
                  <div className={classes.flexCenter}>
                    <MyTypography
                      variant={"captionBold"}
                      align={"center"}
                      className={classes.text}
                    >
                      Select
                    </MyTypography>
                    <Checkbox
                      className={classes.checkBox}
                      size={"small"}
                      checked={listElements.length === listOrderLineId.length}
                      indeterminate={
                        listElements.length > 0 &&
                        listElements.length < listOrderLineId.length
                      }
                      onClick={() => handleCheckboxAll()}
                    />
                  </div>
                </TableCellHeader>
                <TableCellHeader
                  className={classNames(
                    classes.col90,
                    classes.cellFreeze2_Admin
                  )}
                >
                  <Box
                    display={"flex"}
                    alignItems="center"
                    justifyContent={"center"}
                  >
                    <SVG src={printIcon} />
                    <Box mr={1} />
                    <MyTypography variant="subtitle3">
                      {intl.formatMessage({ defaultMessage: "Print" })}
                    </MyTypography>
                  </Box>
                </TableCellHeader>
                <TableCellHeader
                  className={classNames(
                    classes.col150,
                    classes.cellFreeze3_Admin
                  )}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Company Name" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classNames(
                    classes.col280,
                    classes.cellFreeze4_Admin
                  )}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Sold to" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3" align={"center"}>
                    {intl.formatMessage({ defaultMessage: "Order" })}
                    <br />
                    {intl.formatMessage({ defaultMessage: "Create Date" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  direction={
                    sort.sort === OrderConfirmListSortField.soNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(OrderConfirmListSortField.soNo)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "S/O No." })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "PO No." })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "สถานะ" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "ลำดับที่" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col120}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "Material Code" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader className={classes.col320}>
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "รายละเอียดสินค้า" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "จำนวนสั่ง" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "พร้อมส่ง" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "รอผลิต" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({ defaultMessage: "หน่วย" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "วันที่ต้องการ สินค้า"
                    })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  className={classes.col120}
                  textAlign={"center"}
                  align={"center"}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "วันที่ประมาณ การส่งมอบ"
                    })}
                  </MyTypography>
                </TableCellHeader>
              </TableRow>
            </TableHead>
            {renderCollection(data, item => (
              <TableBody key={item.soNo}>
                {renderCollection(item.orderLines, (line, index) => (
                  <TableRow key={`${item.soNo}_${index}`}>
                    <TableCell
                      align="center"
                      className={classNames(
                        classes.borderRight,
                        classes.cellFreeze1_Admin,
                        classes.zIndex1
                      )}
                    >
                      <Checkbox
                        size={"small"}
                        checked={isSelected(`${item.soNo}_${line.itemNo}`)}
                        onClick={() => toggle(`${item.soNo}_${line.itemNo}`)}
                      />
                    </TableCell>
                    <TableCell
                      className={classNames(
                        classes.borderRight,
                        classes.cellFreeze2_Admin,
                        classes.zIndex1
                      )}
                    >
                      {index === 0 && (
                        <MyTypography variant="subtitle3">
                          <Button
                            onClick={handlePrintOrderConfirm}
                            variant={"outlined"}
                            color="secondary"
                            className={classes.btnPrint}
                          >
                            Print
                          </Button>
                          <a id="download" download>
                            &nbsp;
                          </a>
                        </MyTypography>
                      )}
                    </TableCell>
                    <TableCell
                      className={classNames(
                        classes.borderRight,
                        classes.cellFreeze3_Admin,
                        classes.zIndex1
                      )}
                    >
                      <MyTypography variant="body3">
                        {line.saleOrg} - {item.salesOrgName}
                      </MyTypography>
                    </TableCell>
                    <TableCell
                      className={classNames(
                        classes.borderRight,
                        classes.cellFreeze4_Admin,
                        classes.zIndex1
                      )}
                    >
                      <MyTypography variant="body3">
                        {`${line.soldToCode} - ${line.soldToName}`}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      {index === 0 && (
                        <MyTypography variant={"body3"}>
                          {item.orderDate}
                        </MyTypography>
                      )}
                    </TableCell>
                    <TableCell>
                      {index === 0 && (
                        <Link onClick={() => viewOrder(item)}>
                          <MyTypography
                            variant={"subtitle3"}
                            className={classes.soNO}
                          >
                            {item.soNo}
                          </MyTypography>
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>
                      {index === 0 && (
                        <MyTypography variant={"body3"}>
                          {item.poNo}
                        </MyTypography>
                      )}
                    </TableCell>
                    <TableCell align={"center"}>
                      {renderStatus(line?.status)}
                    </TableCell>
                    <TableCell>
                      <MyTypography variant={"body3"}>
                        {line.itemNo}
                      </MyTypography>
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      <MyTypography variant={"body3"}>
                        {line?.materialCode}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant={"body3"}>
                        {line.materialDescription}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography variant="body3">
                        {line.orderQty}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography variant="body3">
                        {line.confirmQuantity}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography variant="body3">
                        {line.nonConfirmQuantity}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography variant="body3">
                        {line.saleUnit}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography variant="body3">
                        {line.requestDate}
                      </MyTypography>
                    </TableCell>
                    <TableCell align={"center"}>
                      <MyTypography
                        variant="body3"
                        className={classes.estimatedDate}
                      >
                        {line.confirmDate}
                      </MyTypography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ))}
          </Table>
        </TableContainer>
        {data.length > 0 && (
          <StickyHorizontalScrollBar container={containerRef} />
        )}
      </div>
      <Box className={classes.paginateFooter}>
        <TablePaginationCustom
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onPreviousPage={onPreviousPage}
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount}
          disabled={disabled}
          isBackFistPage
          isNextLastPage
          selectColor="#007E92"
          rowNumberSet={BIG_ROW_NUMBER_SET}
        />
      </Box>
      <BottomBarSendMail
        selectedLine={listElements.length}
        disabled={listElements.length === 0}
        onSendMail={() => setOpenSendMail(true)}
      />
      <ModalSendMailMultiple
        soldToList={soldToList}
        open={openSendMail}
        onClose={() => setOpenSendMail(false)}
        dataSearchList={dataSearchList}
        filter={filter}
        reportLanguage={selectedReportLanguage}
      />
    </>
  );
};

export default OrderConfirmListTable;
